import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Typography } from 'antd';
import { useNavigate } from 'react-router';
import { useSignOut } from '../../auth/use-session';

/**
 * Idle sign-out for a shared office machine (docs/07-implementation-roadmap.md F1.11).
 *
 * The refresh cookie is good for thirty days and the client renews the access
 * token silently (F1.3), so a session left open on the office's front-desk
 * machine stays open — over lunch, overnight, and for whoever sits down next.
 * That machine can see every member record in the church.
 *
 * So inactivity ends the session, and it ends it **with a warning**. Signing
 * someone out silently while they are reading a screen, or halfway through
 * typing a member's address, loses their work and teaches them the app is
 * unreliable; a minute's notice with a button to stay costs nothing and
 * preserves both.
 */

/** No pointer, key or scroll for this long and the warning appears. */
export const IDLE_LIMIT_MS = 15 * 60 * 1000;
/** How long the warning stands before the session is ended. */
export const IDLE_GRACE_MS = 60 * 1000;
/** How often the idle clock is inspected. Coarse on purpose — this is not a stopwatch. */
const TICK_MS = 1000;

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

export function IdleSignOut({
  idleLimitMs = IDLE_LIMIT_MS,
  graceMs = IDLE_GRACE_MS,
}: {
  idleLimitMs?: number;
  graceMs?: number;
}) {
  const navigate = useNavigate();
  const signOut = useSignOut();
  /** When the warning went up, or `null` while the user is considered present. */
  const [warningSince, setWarningSince] = useState<number | null>(null);
  const [remainingMs, setRemainingMs] = useState(graceMs);
  // Written only from effects and event handlers, never during render: it is
  // not something the UI is drawn from, which is exactly what a ref is for.
  const lastActivity = useRef<number | null>(null);

  const endSession = useCallback(async () => {
    await signOut();
    void navigate('/masuk', { replace: true, state: { notice: 'idleTimeout' } });
  }, [navigate, signOut]);

  const staySignedIn = useCallback(() => {
    lastActivity.current = Date.now();
    setWarningSince(null);
  }, []);

  useEffect(() => {
    lastActivity.current = Date.now();
  }, []);

  useEffect(() => {
    // Activity while the warning is up does **not** dismiss it, which is why
    // the listeners come off entirely for the duration: a stray scroll from
    // someone walking past the desk is not the account's owner saying they are
    // still there.
    if (warningSince !== null) return;

    const note = () => {
      lastActivity.current = Date.now();
    };

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, note, { passive: true });
    }
    return () => {
      for (const event of ACTIVITY_EVENTS) window.removeEventListener(event, note);
    };
  }, [warningSince]);

  useEffect(() => {
    const tick = window.setInterval(() => {
      const now = Date.now();

      if (warningSince === null) {
        if (lastActivity.current !== null && now - lastActivity.current >= idleLimitMs) {
          setWarningSince(now);
          setRemainingMs(graceMs);
        }
        return;
      }

      const remaining = graceMs - (now - warningSince);
      setRemainingMs(Math.max(0, remaining));
      if (remaining <= 0) {
        // Clear the warning first: `endSession` unmounts this tree via the
        // redirect, and a modal still open when its host goes away leaves the
        // page scroll-locked.
        setWarningSince(null);
        void endSession();
      }
    }, TICK_MS);

    return () => window.clearInterval(tick);
  }, [endSession, graceMs, idleLimitMs, warningSince]);

  return (
    <Modal
      open={warningSince !== null}
      title="Masih di sana?"
      okText="Ya, lanjutkan"
      cancelText="Keluar sekarang"
      onOk={staySignedIn}
      onCancel={() => {
        setWarningSince(null);
        void endSession();
      }}
      closable={false}
      // The countdown has to be answered, not dismissed: clicking the backdrop
      // or pressing Escape is not someone saying they are still at the desk.
      mask={{ closable: false }}
      keyboard={false}
    >
      <Typography.Paragraph>
        Karena tidak ada aktivitas, Anda akan otomatis keluar dalam{' '}
        <Typography.Text strong>{Math.ceil(remainingMs / 1000)} detik</Typography.Text>.
      </Typography.Paragraph>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
        Ini menjaga data jemaat tetap aman bila komputer ini ditinggalkan. Pekerjaan yang belum disimpan akan hilang,
        jadi pilih “Ya, lanjutkan” bila Anda masih bekerja.
      </Typography.Paragraph>
    </Modal>
  );
}
